import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { Assignment, ComplaintRecord } from '@api/models/assignment.model';
import { AssignmentsService } from '@api/services/assignments.service';
import { CardComponent } from '@ui/card/card.component';
import { IconComponent } from '@ui/icon/icon.component';
import { ModalService } from '@ui/modal/modal.service';
import { finalize } from 'rxjs';
import { ComplaintModalComponent } from '../complaint-modal/complaint-modal.component';

@Component({
  selector: 'app-assignments-complaints',
  standalone: true,
  imports: [CommonModule, CardComponent, DatePipe, IconComponent],
  template: `
    <ui-card [title]="'Klachten'">
      @if (loading()) {
        <div class="space-y-3">
          <div class="h-4 bg-slate-200 rounded animate-pulse w-3/4"></div>
          <div class="h-4 bg-slate-200 rounded animate-pulse w-1/2"></div>
        </div>
      } @else if (complaints().length) {
        @for (complaint of complaints(); track $index) {
          <div
            class="flex items-center justify-between p-3 hover:bg-gray-50 rounded transition-colors cursor-pointer group"
            (click)="openComplaintModal(complaint)"
          >
            <div class="flex items-center gap-3 min-w-0">
              <ui-icon [name]="'document-text'" [color]="'secondary'"></ui-icon>
              <div class="min-w-0">
                <div class="text-sm font-medium truncate">
                  {{ complaint.subject ?? 'Klacht' }}
                </div>
                <div class="text-xs text-gray-500">
                  @if (complaint.dossier_item) {
                    <span>Klachtnummer: {{ complaint.dossier_item }}</span>
                  }
                  @if (complaint.dossier_item && complaint.instuur_datum) {
                    <span> · </span>
                  }
                  @if (complaint.instuur_datum) {
                    <span>{{ complaint.instuur_datum | date : 'dd-MM-yyyy' }}</span>
                  }
                </div>
              </div>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              @if (complaint.status) {
                <span [class]="getStatusClass(complaint.status)">
                  {{ complaint.status }}
                </span>
              }
              @if (complaint.attachments?.length) {
                <ui-icon [name]="'document-text'" class="w-4 h-4 text-gray-400"></ui-icon>
              }
            </div>
          </div>
        }
      } @else {
        <p class="text-sm text-gray-500 py-2">Geen klachten</p>
      }
    </ui-card>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
})
export class AssignmentsComplaintsComponent {
  private assignmentsService = inject(AssignmentsService);
  private modalService = inject(ModalService);
  private cdr = inject(ChangeDetectorRef);
  assignment = input.required<Assignment | null>();

  complaints = signal<ComplaintRecord[]>([]);
  loading = signal(true);

  constructor() {
    effect(
      () => {
        const assignmentId = this.assignment()?.id;
        const personID = this.assignment()?.details?.id;
        if (assignmentId && personID) {
          this.getComplaints(assignmentId, personID);
        } else {
          this.complaints.set([]);
          this.loading.set(false);
        }
      },
      { allowSignalWrites: true }
    );
  }

  getComplaints(assignmentId: string, personID: string) {
    this.loading.set(true);
    this.assignmentsService
      .getAssignmentComplaintDossiers(assignmentId, personID)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.complaints.set(response.complaint_records ?? []);
          this.cdr.markForCheck();
        },
        error: () => {
          this.complaints.set([]);
          this.cdr.markForCheck();
        },
      });
  }

  openComplaintModal(complaint: ComplaintRecord) {
    this.modalService.open(ComplaintModalComponent, {
      data: {
        item: complaint,
        assignmentId: this.assignment()?.id,
      },
    });
  }

  getStatusClass(status: string): string {
    const baseClasses = 'px-2.5 py-0.5 rounded-full text-xs font-medium';
    const normalized = status.toLowerCase();

    if (normalized.includes('afgehandeld') || normalized.includes('opgelost')) {
      return `bg-green-100 text-green-800 ${baseClasses}`;
    }
    if (normalized.includes('open') || normalized.includes('lopend')) {
      return `bg-yellow-100 text-yellow-800 ${baseClasses}`;
    }
    return `bg-gray-100 text-gray-800 ${baseClasses}`;
  }
}
